import { useQuery, useQueryClient, useMutation, skipToken } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import useSession from '@hooks/useSession'
import { fetchPhoto, uploadPhoto, deletePhoto as deletePhotoApi } from '@/services/contribution/api'
import { InvalidSessionError } from '@/util/errors'

type PhotoMutationSuccessCallback = (data: { photoUrl: string }) => void
type PhotoMutationErrorCallback = (error: Error) => void
interface UploadPhotoProps {
  photo: File
  onMutationSuccess?: PhotoMutationSuccessCallback
  onMutationError?: PhotoMutationErrorCallback
  onMutationSettled?: () => void
}

interface DeletePhotoProps {
  onMutationSuccess?: () => void
  onMutationError?: PhotoMutationErrorCallback
  onMutationSettled?: () => void
}

export default function usePhoto() {
  const queryClient = useQueryClient()

  const { session } = useSession()

  const {
    data: photo,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['photo'],
    queryFn: session ? () => fetchPhoto(session.participationId) : skipToken,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!session,
  })

  const uploadPhotoMutation = useMutation({
    mutationFn: ({ photo, participationId }: { photo: File; participationId: string }) =>
      uploadPhoto(participationId, photo),
  })

  function setPhoto({ photo, onMutationSuccess, onMutationError, onMutationSettled }: UploadPhotoProps): void {
    if (!session) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Invalid session',
        level: 'error',
        data: {
          file: 'hooks/setPhoto Mutation',
        },
      })
      throw new InvalidSessionError('No session available')
    }

    uploadPhotoMutation.mutate(
      { photo, participationId: session.participationId },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries({ queryKey: ['photo'] })
          queryClient.setQueryData(['photo'], data.photoUrl)
          if (onMutationSuccess) {
            onMutationSuccess(data)
          }
        },
        onError: (error) => {
          if (onMutationError) {
            onMutationError(error)
          }
        },
        onSettled: () => {
          if (onMutationSettled) {
            onMutationSettled()
          }
        },
      }
    )
  }

  const deletePhotoMutation = useMutation({
    mutationFn: ({ participationId }: { participationId: string }) => deletePhotoApi(participationId),
  })
  function deletePhoto({ onMutationSuccess, onMutationError, onMutationSettled }: DeletePhotoProps) {
    if (!session) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Invalid session',
        level: 'error',
        data: {
          file: 'hooks/deletePhoto Mutation',
        },
      })
      throw new InvalidSessionError('No session available')
    }

    deletePhotoMutation.mutate(
      { participationId: session.participationId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['photo'] })
          queryClient.setQueryData(['photo'], null)
          if (onMutationSuccess) {
            onMutationSuccess()
          }
        },
        onError: (error) => {
          if (onMutationError) {
            onMutationError(error)
          }
        },
        onSettled: () => {
          if (onMutationSettled) {
            onMutationSettled()
          }
        },
      }
    )
  }

  return {
    photo,
    isLoading: isLoading || !session,
    error,
    setPhoto,
    deletePhoto,
  }
}
